import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/axiosConfig';
import useInactivityRedirect from '../hooks/useInactivityRedirect';
import { useNavigate } from 'react-router-dom';
import './Checkin.css';

const Checkin = () => {
  useInactivityRedirect(3); // Redirect after 3 minutes of inactivity
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    trailerPlate: '',
    truckPlate: '',
    partner: '',
    phone: '',
    driverName: '',
    cpf: '',
    tipo: 'DESCARREGAMENTO' // Add tipo field with default value
  });
  
  const [partners, setPartners] = useState([]);
  const [filteredPartners, setFilteredPartners] = useState([]);
  const [showPartnerDropdown, setShowPartnerDropdown] = useState(false);
  
  const [filteredDrivers, setFilteredDrivers] = useState([]);
  const [showDriverDropdown, setShowDriverDropdown] = useState(false);
  
  const [message, setMessage] = useState('');
  const partnerInputRef = useRef(null);
  const driverInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const messageTimeoutRef = useRef(null);

  // Get user role
  const user = JSON.parse(localStorage.getItem('user'));
  const isVisualizador = user && user.role === 'Visualizador';
  const isSupervisor = user && user.role === 'Supervisor';

  // Redirect if user is Visualizador
  useEffect(() => {
    if (isVisualizador) {
      navigate('/dashboard');
    }
  }, [isVisualizador, navigate]);

  // Fetch partners for autocomplete
  useEffect(() => {
    // Don't fetch data if user is Visualizador
    if (isVisualizador) return;
    
    const fetchPartners = async () => {
      try {
        const res = await api.get('/partners');
        setPartners(res.data);
      } catch (error) {
        console.error('Error fetching partners:', error);
      }
    };
    
    fetchPartners();
  }, [isVisualizador]);

  // Format phone number for display (remove +55 prefix if present)
  const formatPhoneForDisplay = (phone) => {
    if (!phone) return '';
    // Remove +55 prefix if it exists for display purposes
    if (phone.startsWith('+55')) {
      return phone.substring(3);
    }
    return phone;
  };

  // Format phone number for submission (add +55 prefix if needed)
  const formatPhoneForSubmission = (phone) => {
    if (!phone) return '';
    // If phone already starts with +55, return as is
    if (phone.startsWith('+55')) {
      return phone;
    }
    // If phone starts with 55 (without +), add the +
    if (phone.startsWith('55')) {
      return `+${phone}`;
    }
    // Otherwise, add +55 prefix (assuming it's a Brazilian number)
    return `+55${phone}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle phone input specially
    if (name === 'phone') {
      // For phone field, we store the value as entered but will format on submission
      setFormData({
        ...formData,
        [name]: value
      });
      return;
    }
    
    // Handle driver name search
    if (name === 'driverName') {
      setFormData({
        ...formData,
        [name]: value
      });
      
      // Clear previous timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      // If we have a value, show suggestions immediately and debounce further searches
      if (value.length > 0) {
        // Show suggestions immediately for better UX
        const searchDrivers = async () => {
          try {
            const res = await api.get(`/trucks/search/drivers?query=${value}`);
            setFilteredDrivers(res.data);
            setShowDriverDropdown(true);
          } catch (error) {
            console.error('Error searching drivers:', error);
            setFilteredDrivers([]);
            setShowDriverDropdown(false);
          }
        };
        
        // Show suggestions immediately for the first character
        if (value.length === 1) {
          searchDrivers();
        } else {
          // Debounce for subsequent characters
          searchTimeoutRef.current = setTimeout(() => {
            searchDrivers();
          }, 300); // 300ms debounce
        }
      } else if (value.length === 0) {
        setFilteredDrivers([]);
        setShowDriverDropdown(false);
      }
      return;
    }
    
    // Handle partner search
    if (name === 'partner') {
      // For partner field, we store the search text temporarily
      setFormData({
        ...formData,
        [name]: value
      });
      
      // Clear previous timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      if (value.length > 1) {
        // Debounce the search to prevent too many API calls
        searchTimeoutRef.current = setTimeout(() => {
          // Use the search endpoint for better performance
          const searchPartners = async () => {
            try {
              const res = await api.get(`/trucks/search/partners?query=${value}`);
              setFilteredPartners(res.data);
              setShowPartnerDropdown(true);
            } catch (error) {
              console.error('Error searching partners:', error);
              // Fallback to client-side filtering
              const filtered = partners.filter(partner => 
                partner.name.toLowerCase().includes(value.toLowerCase())
              );
              setFilteredPartners(filtered);
              setShowPartnerDropdown(true);
            }
          };
          searchPartners();
        }, 300); // 300ms debounce
      } else if (value.length === 0) {
        setFilteredPartners([]);
        setShowPartnerDropdown(false);
      }
    } else {
      // For other fields, update normally
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handlePartnerSelect = (partner) => {
    setFormData({
      ...formData,
      partner: partner._id
    });
    setShowPartnerDropdown(false);
    // Focus next field after selection
    setTimeout(() => {
      document.getElementById('phone')?.focus();
    }, 100);
  };
  
  const handleDriverSelect = (driver) => {
    // Find the partner object if partner ID is provided
    let partnerId = driver.partner || '';
    if (partnerId && partners.length > 0) {
      const partnerExists = partners.find(p => p._id === partnerId);
      if (!partnerExists) {
        partnerId = '';
      }
    }
    
    setFormData({
      ...formData,
      driverName: driver.driverName,
      cpf: driver.cpf || '',
      phone: driver.phone ? formatPhoneForDisplay(driver.phone) : '',
      trailerPlate: driver.trailerPlate || '',
      truckPlate: driver.truckPlate || '',
      partner: partnerId
    });
    setShowDriverDropdown(false);
    // Focus next field after selection
    setTimeout(() => {
      document.getElementById('cpf')?.focus();
    }, 100);
  };

  // Handle keyboard navigation in the dropdown
  const handlePartnerKeyDown = (e) => {
    if (showPartnerDropdown && filteredPartners.length > 0) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        // Keyboard navigation could be implemented here
      } else if (e.key === 'Enter') {
        e.preventDefault();
        // Select the first partner in the list
        if (filteredPartners.length > 0) {
          handlePartnerSelect(filteredPartners[0]);
        }
      } else if (e.key === 'Escape') {
        setShowPartnerDropdown(false);
      }
    }
  };
  
  // Handle keyboard navigation in the driver dropdown
  const handleDriverKeyDown = (e) => {
    if (showDriverDropdown && filteredDrivers.length > 0) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        // Keyboard navigation could be implemented here
      } else if (e.key === 'Enter') {
        e.preventDefault();
        // Select the first driver in the list
        if (filteredDrivers.length > 0) {
          handleDriverSelect(filteredDrivers[0]);
        }
      } else if (e.key === 'Escape') {
        setShowDriverDropdown(false);
      }
    }
  };

  // Handle clicking outside the dropdown to close it
  const handleOutsideClick = (e) => {
    if (showPartnerDropdown && !e.target.closest('.autocomplete-wrapper')) {
      setShowPartnerDropdown(false);
    }
    if (showDriverDropdown && !e.target.closest('.autocomplete-wrapper')) {
      setShowDriverDropdown(false);
    }
  };

  // Add event listener for outside clicks
  useEffect(() => {
    const handleClick = (e) => {
      handleOutsideClick(e);
    };
    
    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [handleOutsideClick]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear any existing message timeout
    if (messageTimeoutRef.current) {
      clearTimeout(messageTimeoutRef.current);
    }
    
    // Validate that a partner is selected (only required for DESCARREGAMENTO type)
    if (formData.tipo === 'DESCARREGAMENTO' && (!formData.partner || !partners.find(p => p._id === formData.partner))) {
      setMessage('Por favor, selecione um parceiro válido da lista');
      // Clear error message after 5 seconds
      messageTimeoutRef.current = setTimeout(() => {
        setMessage('');
      }, 5000);
      return;
    }
    
    // Validate driver name is provided
    if (!formData.driverName) {
      setMessage('Por favor, informe o nome do motorista');
      // Clear error message after 5 seconds
      messageTimeoutRef.current = setTimeout(() => {
        setMessage('');
      }, 5000);
      return;
    }
    
    // Prepare form data with formatted phone number
    const submitData = {
      ...formData,
      phone: formatPhoneForSubmission(formData.phone)
    };
    
    try {
      await api.post('/trucks', submitData);
      setMessage('Check-in realizado com sucesso!');
      
      // Clear the success message after 15 seconds
      messageTimeoutRef.current = setTimeout(() => {
        setMessage('');
      }, 15000);
      
      setFormData({
        trailerPlate: '',
        truckPlate: '',
        partner: '',
        phone: '',
        driverName: '',
        cpf: '',
        tipo: 'DESCARREGAMENTO' // Reset to default value
      });
      setFilteredPartners([]);
      setFilteredDrivers([]);
      setShowPartnerDropdown(false);
      setShowDriverDropdown(false);
    } catch (error) {
      setMessage('Erro ao realizar check-in: ' + error.response?.data?.message || error.message);
      // Clear error message after 5 seconds
      messageTimeoutRef.current = setTimeout(() => {
        setMessage('');
      }, 5000);
    }
  };

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (messageTimeoutRef.current) {
        clearTimeout(messageTimeoutRef.current);
      }
    };
  }, []);

  // Get the display name for the selected partner
  const getPartnerName = () => {
    if (!formData.partner) return formData.partner || '';
    
    // Check if partner is already selected by ID
    const selectedPartner = partners.find(p => p._id === formData.partner);
    if (selectedPartner) {
      return selectedPartner.name;
    }
    
    // Otherwise, it's the search text
    return formData.partner;
  };

  return (
    <div className="checkin-container">
      {isVisualizador ? (
        <div>Access denied. Visualizador role cannot access this page.</div>
      ) : (
        <>
          <h2>Check-in de Motorista</h2>
          
          {message && (
            <div className={`message ${message.includes('sucesso') ? 'success' : 'error'}`}>
              {message}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="checkin-form">
            <div className="form-group">
              <label htmlFor="driverName">Nome do Motorista *</label>
              <div className="autocomplete-wrapper">
                <input
                  ref={driverInputRef}
                  type="text"
                  id="driverName"
                  name="driverName"
                  value={formData.driverName}
                  onChange={handleInputChange}
                  onKeyDown={handleDriverKeyDown}
                  onFocus={() => {
                    if (formData.driverName.length > 0) {
                      // Trigger search immediately when focusing with content
                      const searchDrivers = async () => {
                        try {
                          const res = await api.get(`/trucks/search/drivers?query=${formData.driverName}`);
                          setFilteredDrivers(res.data);
                          setShowDriverDropdown(true);
                        } catch (error) {
                          console.error('Error searching drivers:', error);
                          setFilteredDrivers([]);
                          setShowDriverDropdown(false);
                        }
                      };
                      searchDrivers();
                    }
                  }}
                  placeholder="Digite o nome do motorista..."
                  required
                  autoComplete="off"
                />
                {showDriverDropdown && (
                  <div className="autocomplete-dropdown">
                    {filteredDrivers.length > 0 ? (
                      filteredDrivers.map(driver => (
                        <div
                          key={`${driver.driverName}-${driver.cpf}`}
                          className="autocomplete-item"
                          onClick={() => handleDriverSelect(driver)}
                        >
                          <div className="driver-name">{driver.driverName}</div>
                          <div className="driver-details">
                            {driver.cpf && <span>CPF: {driver.cpf}</span>}
                            {driver.phone && <span>Tel: {formatPhoneForDisplay(driver.phone)}</span>}
                            {driver.trailerPlate && <span>Placa: {driver.trailerPlate}</span>}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="autocomplete-item no-results">
                        Nenhum motorista encontrado
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="cpf">CPF</label>
              <input
                type="text"
                id="cpf"
                name="cpf"
                value={formData.cpf}
                onChange={handleInputChange}
                placeholder="Digite o CPF do motorista"
                maxLength="14"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="trailerPlate">Placa da Carreta *</label>
              <input
                type="text"
                id="trailerPlate"
                name="trailerPlate"
                value={formData.trailerPlate}
                onChange={handleInputChange}
                placeholder="Digite a placa da carreta"
                required
                maxLength="7"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="truckPlate">Placa do Cavalo</label>
              <input
                type="text"
                id="truckPlate"
                name="truckPlate"
                value={formData.truckPlate}
                onChange={handleInputChange}
                placeholder="Digite a placa do cavalo"
                maxLength="7"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="tipo">Tipo *</label>
              <select
                id="tipo"
                name="tipo"
                value={formData.tipo}
                onChange={handleInputChange}
                required
              >
                <option value="DESCARREGAMENTO">Descarregamento</option>
                <option value="GIRO">Giro</option>
                <option value="CAPTAÇÃO">Captação</option>
                <option value="AGREGADO">Agregado</option>
                <option value="CARREGAMENTO">Carregamento</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="partner">
                Parceiro 
                {formData.tipo === 'DESCARREGAMENTO' && <span className="required">*</span>}
              </label>
              <div className="autocomplete-wrapper">
                <input
                  ref={partnerInputRef}
                  type="text"
                  id="partner"
                  name="partner"
                  value={getPartnerName()}
                  onChange={handleInputChange}
                  onKeyDown={handlePartnerKeyDown}
                  onFocus={() => {
                    if (getPartnerName().length > 0) {
                      const filtered = partners.filter(partner => 
                        partner.name.toLowerCase().includes(getPartnerName().toLowerCase())
                      );
                      setFilteredPartners(filtered);
                      setShowPartnerDropdown(true);
                    }
                  }}
                  placeholder="Digite para buscar parceiro..."
                  required={formData.tipo === 'DESCARREGAMENTO'}
                  autoComplete="off"
                />
                {showPartnerDropdown && (
                  <div className="autocomplete-dropdown">
                    {filteredPartners.length > 0 ? (
                      filteredPartners.map(partner => (
                        <div
                          key={partner._id}
                          className="autocomplete-item"
                          onClick={() => handlePartnerSelect(partner)}
                        >
                          <div className="partner-name">{partner.name}</div>
                          <div className="partner-details">
                            {partner.contact && <span>{partner.contact}</span>}
                            {partner.phone && <span>{partner.phone}</span>}
                          </div>
                        </div>

                      ))
                    ) : (
                      <div className="autocomplete-item no-results">
                        Nenhum parceiro encontrado
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="phone">Telefone</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formatPhoneForDisplay(formData.phone)}
                onChange={handleInputChange}
                placeholder="Ex: 11999999999"
              />
            </div>
            
            <button type="submit" className="submit-button">Realizar Check-in</button>
          </form>
        </>
      )}
    </div>
  );
};

export default Checkin;